import React, { useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { TemplateField, SUPPORTED_LANGUAGES } from '../types/template';
import FormRenderer from './FormRenderer';

interface Props {
    show: boolean;
    onHide: () => void;
    name: string;
    description?: string | null;
    fields: TemplateField[];
    languages?: string[];
    names?: Record<string, string>;
    descriptions?: Record<string, string>;
}

export default function FormPreviewModal({ show, onHide, name, description, fields, languages = ['en'], names = {}, descriptions = {} }: Props) {
    const [submitted, setSubmitted] = useState(false);
    const [previewKey, setPreviewKey] = useState(0);
    const [currentLang, setCurrentLang] = useState(languages[0] ?? 'en');

    const displayName = names[currentLang] || names['en'] || name;
    const displayDesc = descriptions[currentLang] || descriptions['en'] || description;
    const langName = (code: string) => SUPPORTED_LANGUAGES.find(l => l.code === code)?.name ?? code.toUpperCase();

    const handleClose = () => { setSubmitted(false); onHide(); };
    const handleReset = () => { setSubmitted(false); setPreviewKey(k => k + 1); };

    return (
        <Modal
            show={show}
            onHide={handleClose}
            size="xl"
            className="template-preview-modal"
            scrollable
        >
            {/* Minimal header — just close button + preview badge */}
            <Modal.Header closeButton className="template-preview-modal__topbar">
                <span className="badge bg-warning text-dark" style={{ fontSize: 11 }}>
                    PREVIEW MODE
                </span>
            </Modal.Header>

            <Modal.Body className="p-0">
                {/* ── Same card layout as the real fill page ── */}
                <div className="template-fill-page__card template-preview-modal__card">

                    {/* Red header */}
                    <div className="template-fill-page__header">
                        <img src="/logo-pandora.png" alt="Pandora" className="template-fill-page__header-logo" />

                        {languages.length > 1 && (
                            <div className="form-lang-toggle">
                                {languages.map(l => (
                                    <button
                                        key={l}
                                        type="button"
                                        className={`form-lang-toggle__btn${currentLang === l ? ' form-lang-toggle__btn--active' : ''}`}
                                        onClick={() => setCurrentLang(l)}
                                    >
                                        {langName(l)}
                                    </button>
                                ))}
                            </div>
                        )}

                        <h2>{displayName}</h2>
                        {displayDesc && <p>{displayDesc}</p>}
                    </div>

                    {/* Body */}
                    <div className="template-fill-page__body">
                        {/* Preview notice */}
                        <div className="template-preview-modal__notice">
                            โหมด Preview — ตอบได้เหมือนจริง แต่ไม่มีการบันทึกข้อมูล
                        </div>

                        {submitted ? (
                            <div className="text-center py-5">
                                <div style={{ fontSize: 56 }}>✅</div>
                                <h4 className="mt-3 fw-bold">Response Submitted! (Preview)</h4>
                                <p className="text-muted mb-4">ข้อมูลไม่ถูกบันทึกจริง — นี่คือโหมด Preview</p>
                                <Button variant="outline-secondary" onClick={handleReset}>
                                    ลองกรอกใหม่
                                </Button>
                            </div>
                        ) : fields.length === 0 ? (
                            <div className="text-center text-muted py-5">
                                No fields added yet. Add some components in the builder.
                            </div>
                        ) : (
                            <FormRenderer
                                key={previewKey}
                                schema={{ fields, languages, names, descriptions }}
                                readOnly={false}
                                onSubmit={() => setSubmitted(true)}
                                submitLabel="Submit"
                                currentLang={currentLang}
                            />
                        )}
                    </div>

                    {/* Footer — same as real page */}
                    <footer className="template-fill-page__footer">
                        <div className="template-fill-page__footer-inner">
                            <div className="template-fill-page__footer-brand">
                                <img src="/logo-pandora.png" alt="Pandora" className="template-fill-page__footer-logo" />
                                <span className="template-fill-page__footer-sub">HR Event Management System</span>
                            </div>
                            <div className="template-fill-page__footer-meta">
                                <span>🔒 Internal Use Only</span>
                                <span className="template-fill-page__footer-dot">·</span>
                                <span>© {new Date().getFullYear()} Pandora</span>
                            </div>
                        </div>
                    </footer>
                </div>
            </Modal.Body>
        </Modal>
    );
}
